/* eslint-disable max-lines */
import { Box, Chip, Icon, IconButton, Tooltip, Typography } from '@mui/material';
import {
    DataGridPro,
    GridApi,
    GridColDef,
    GridRenderCellParams,
    GridSortModel,
    GridValueFormatterParams,
    DataGridProProps,
    GRID_CHECKBOX_SELECTION_COL_DEF,
} from '@mui/x-data-grid-pro';
import React from 'react';
import { Observable } from 'rxjs';

import { GeneralConstants } from 'app/constants';
import { Channel } from 'app/constants/channel';
import { TimingConstants } from 'app/constants/timing-constants';
import { AppContext } from 'app/context/app-context';
import { AppContextProps } from 'app/context/app-context-props';
import { LocalStorageKeys } from 'app/enum';
import { AppConfigName } from 'app/enum/app-config-name';
import { GridApiRefProp, withDataGridApi } from 'app/hocs/grid-api-ref';
import { AppConfig } from 'app/model/app-config';
import { Store } from 'app/model/cris/store';
import { MidTuple } from 'app/model/mid-tuple';
import { PatternData } from 'app/model/pattern-data';
import { WithEsId } from 'app/type/with-es-id';
import { DataGridFormatters } from 'app/utils/data-grid-formatters';
import { Formatters } from 'app/utils/formatters';

import { FilterVersion } from './ml-training-filter';
import { TupleCell } from './tuple-cell';

export interface TupleTableProps extends GridApiRefProp {
    tuples: Array<Array<WithEsId<MidTuple>>>;
    sortModel?: GridSortModel;
    totalTrx?: number;
    totalSpend?: number;
    /**
     * Show or hide store and brand by default.
     */
    storeBrandVisibility: boolean;
    mlVisibility?: boolean;
    tagHighlightsVisibility?: boolean;
    highlightField?: string;
    highlightPattern?: string;
    highlightChannel?: Channel;

    onFill?: (field: keyof PatternData, value: unknown) => void;
    onSort: (field?: keyof MidTuple, direction?: 'asc' | 'desc') => void;
    onLoadMore: () => Observable<unknown>;
    checkboxSelection?: DataGridProProps['checkboxSelection'];
    // eslint-disable-next-line @typescript-eslint/naming-convention
    MlTrainingActionsBarSlot?: React.ReactNode;
    onRowSelectionModelChange?: DataGridProProps['onRowSelectionModelChange'];
    hideDefaultHeader?: boolean;
    primaryStore?: Store;
    mlTrainingFilterVersion?: FilterVersion;
}

const TOP_SPACING = 160;

class TupleResultsTable extends React.Component<TupleTableProps> {
    public static contextType = AppContext;
    private static nextInstanceIndex = 0;
    private loadingMore = false;
    private columnDragging = false;
    private instanceClassName: string;
    private highlightRegex?: RegExp;
    private trxCountFormatter!: (params: GridValueFormatterParams) => string;
    private spendFormatter!: (params: GridValueFormatterParams) => string;

    public constructor(props: Readonly<TupleTableProps> | TupleTableProps) {
        super(props);

        this.setCountFormatters(props);

        this.updateRegexExpr();
        this.instanceClassName = `tuple-results-${TupleResultsTable.nextInstanceIndex++}`;
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type, @typescript-eslint/explicit-module-boundary-types
    public componentDidUpdate(prevProps: Readonly<TupleTableProps>) {
        if (this.props.highlightPattern !== prevProps.highlightPattern) {
            this.updateRegexExpr();
        }

        this.setCountFormatters(this.props);
    }

    public setCountFormatters(props: Readonly<TupleTableProps>): void {
        const makeFormatter = (refValue?: number) => {
            if (refValue) {
                return (params: GridValueFormatterParams): string => {
                    const percent = Formatters.percent(Number(params.value) / refValue, 1);

                    return `(${percent}%) ${DataGridFormatters.numberLocaleFormatter(params)}`;
                };
            }

            return DataGridFormatters.numberLocaleFormatter;
        };

        this.trxCountFormatter = makeFormatter(props.totalTrx);
        this.spendFormatter = makeFormatter(props.totalSpend);
    }

    public componentDidMount(): void {
        this.loadingMore = false;
        const api = this.props.gridApiRef.current;
        api.subscribeEvent('columnHeaderDragStart', () => (this.columnDragging = true));
        api.subscribeEvent('columnHeaderDragEnd', () => {
            this.columnDragging = false;
            TupleResultsTable.storeColumnOrder(api);
        });
        api.subscribeEvent('rowsScrollEnd', () => {
            if (this.loadingMore) {
                // nothing to do
                return;
            }

            if (!this.props.tuples[0]?.length) {
                // first page did not load yet, prevent early loading here
                return;
            }

            this.loadingMore = true;
            this.props
                .onLoadMore()
                .pipe()
                .subscribe({
                    complete: () => {
                        // add a slight delay to avoid event spam when we reach the end of the list
                        setTimeout(() => (this.loadingMore = false), TimingConstants.TYPING_DELAY);
                    },
                });
        });
    }

    public render(): React.ReactNode {
        const channelMethod = (this.context as AppContextProps).appConfigs[AppConfigName.CHANNEL_METHOD] as AppConfig<
            Record<number, string | undefined>
        >;
        const matchMethod = (this.context as AppContextProps).appConfigs[AppConfigName.MATCH_METHOD] as AppConfig<
            Record<number, string | undefined>
        >;

        const columns = TupleResultsTable.getOrderedColumns([
            {
                headerName: 'Info',
                field: 'info',
                width: 50,
                disableReorder: true,
                renderCell: (params: GridRenderCellParams<WithEsId<MidTuple> & { opposing?: boolean }>) => {
                    const infoElements: React.ReactNode[] = [];
                    if (params.row.unCorrected) {
                        infoElements.push(
                            <Tooltip
                                key='uncorrected'
                                title={`Affected by disabled pattern ${params.row.correctionPatId}`}
                            >
                                <Icon>info</Icon>
                            </Tooltip>,
                        );
                    }
                    if (params.row.affectedByActivePattern) {
                        infoElements.push(
                            <Tooltip key='affectedByActivePattern' title='Affected by active pattern'>
                                <Icon>warning</Icon>
                            </Tooltip>,
                        );
                    }
                    if (params.row.overlapping) {
                        infoElements.push(
                            <Tooltip key='overlapping' title='Overlaps with selected pattern'>
                                <Icon>warning</Icon>
                            </Tooltip>,
                        );
                    }

                    return 0 < infoElements.length ? (
                        <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>{infoElements}</div>
                    ) : undefined;
                },
            },
            {
                headerName: 'Training',
                field: 'mlType',
                width: 110,
                disableReorder: true,
                renderCell: (params: GridRenderCellParams<WithEsId<MidTuple>, MidTuple['mlType']>) => {
                    return params.row.mlTagName ? (
                        <Chip
                            label={
                                'basic' === this.props.mlTrainingFilterVersion
                                    ? 'Trained'
                                    : params.value || 'Unassigned'
                            }
                            size='small'
                            sx={{ textTransform: 'capitalize' }}
                        />
                    ) : (
                        ''
                    );
                },
            },
            {
                headerName: 'Tagged Store',
                field: 'mlTagName',
                width: 120,
                disableReorder: true,
            },
            {
                headerName: 'Stable Name',
                field: 'mlStableName',
                width: 120,
                disableReorder: true,
            },
            {
                headerName: 'Tagged Store Id',
                field: 'mlTagId',
                width: 110,
            },
            {
                headerName: 'Stable Id',
                field: 'mlStableId',
                width: 110,
            },
            {
                headerName: 'Conf Score',
                field: 'mlConfScore',
                width: 110,
            },
            {
                headerName: 'Tag Negative',
                field: 'mlTagNegative',
                width: 110,
            },
            {
                headerName: 'MTID',
                field: 'mtid',
                width: 300,
                valueFormatter: (params: GridValueFormatterParams<WithEsId<MidTuple>['mtid']>) => `mt-${params.value}`,
            },
            {
                headerName: 'MID',
                field: 'mid',
                width: 160,
                renderCell: (params) => this.withFillBtn('midPattern', params),
            },
            {
                headerName: 'Descriptor',
                field: 'merchdesc',
                width: 360,
                renderCell: (params) => this.withFillBtn('descPattern', params),
            },
            { headerName: 'MCC Name', field: 'mccName', width: 200 },
            {
                headerName: 'MCC',
                field: 'mcc',
                width: 88,
                disableColumnMenu: true,
                renderCell: (params) => this.withFillBtn('mccPattern', params, [params.value]),
            },
            {
                headerName: 'Trxs',
                field: 'trxCount',
                width: 120,
                disableColumnMenu: true,
                valueFormatter: (params) => this.trxCountFormatter(params),
            },
            {
                headerName: 'Spend',
                field: 'totalSpend',
                width: 180,
                valueFormatter: (params) => this.spendFormatter(params),
            },
            ...((!this.props.mlVisibility
                ? [
                      {
                          headerName: 'Channel',
                          field: 'channel',
                          width: 109,
                          disableColumnMenu: true,
                          renderCell: (params) => (
                              <TupleCell params={params} highlightChannel={this.props.highlightChannel} />
                          ),
                      },
                  ]
                : []) as Array<GridColDef<WithEsId<MidTuple>>>),
            {
                headerName: 'Store Id',
                field: 'storeId',
                width: 110,
                disableColumnMenu: true,
            },
            {
                headerName: 'Store Name',
                field: 'storeName',
                width: 120,
                disableColumnMenu: true,
            },
            {
                headerName: 'Brand Id',
                field: 'brandId',
                width: 110,
                disableColumnMenu: true,
            },
            {
                headerName: 'Brand Name',
                field: 'brandName',
                width: 120,
                disableColumnMenu: true,
            },
            {
                headerName: 'City',
                field: 'city',
                width: 150,
                renderCell: (params) => this.withFillBtn('cityPattern', params),
            },
            {
                headerName: 'State',
                field: 'state',
                width: 90,
                disableColumnMenu: true,
                renderCell: (params) => this.withFillBtn('statePattern', params),
            },
            {
                headerName: 'Zip',
                field: 'zip',
                width: 88,
                disableColumnMenu: true,
                renderCell: (params) => this.withFillBtn('zipPattern', params),
            },
            {
                headerName: 'Country',
                field: 'country',
                width: 107,
                disableColumnMenu: true,
                renderCell: (params) => this.withFillBtn('countryPattern', params),
            },

            ...((!this.props.mlVisibility
                ? [
                      { headerName: 'Store Num', field: 'storeNum', width: 125, disableColumnMenu: true },
                      { headerName: 'Cor Pat id', field: 'correctionPatId', width: 150, disableColumnMenu: true },
                      {
                          headerName: 'Chan Method',
                          field: 'channelMethod',
                          width: 150,
                          disableColumnMenu: true,
                          valueFormatter: (params: GridValueFormatterParams<WithEsId<MidTuple>['channelMethod']>) => {
                              const value = channelMethod.value[+params.value];
                              if (value !== undefined) {
                                  return value;
                              }

                              return params.value || '';
                          },
                      },
                      { headerName: 'Chan Pat Id', field: 'channelPatId', width: 150, disableColumnMenu: true },
                      {
                          headerName: 'Store Match Method',
                          field: 'storeMatchMethod',
                          width: 200,
                          disableColumnMenu: true,
                          valueFormatter: (
                              params: GridValueFormatterParams<WithEsId<MidTuple>['storeMatchMethod']>,
                          ) => {
                              const value = matchMethod.value[+params.value];
                              if (value !== undefined) {
                                  return value;
                              }

                              return params.value || '';
                          },
                      },
                      {
                          headerName: 'Store Match Source',
                          field: 'storeMatchSource',
                          width: 200,
                          disableColumnMenu: true,
                      },
                      { headerName: 'Store Num Pat Id', field: 'storeNumPatId', width: 180, disableColumnMenu: true },
                  ]
                : []) as Array<GridColDef<WithEsId<MidTuple>>>),
        ] as Array<GridColDef<WithEsId<MidTuple>>>);

        return (
            <Box height={`${window.innerHeight - TOP_SPACING}px`} className={this.instanceClassName}>
                {!this.props.hideDefaultHeader && (
                    <>
                        <Typography variant='h5'>Matches</Typography>
                        <Typography variant='body2'>
                            All fields below (city, state, etc.) related to merchant (not cardholder).
                            {this.props.totalSpend ? (
                                <span>
                                    {' '}
                                    Percentages calculated relative to:{' '}
                                    <strong>
                                        Trxs:
                                        {this.props.totalTrx?.toLocaleString()}, Spend:{' '}
                                        {this.props.totalSpend.toLocaleString()}
                                    </strong>
                                </span>
                            ) : (
                                ''
                            )}
                        </Typography>
                    </>
                )}

                {this.props.MlTrainingActionsBarSlot}
                <Box sx={{ width: '100%', display: 'grid', height: '100%' }}>
                    <DataGridPro
                        apiRef={this.props.gridApiRef}
                        rows={([] as Array<WithEsId<MidTuple>>).concat(...this.props.tuples)}
                        getRowId={(row: WithEsId<MidTuple>) => row.esId}
                        initialState={{
                            columns: {
                                columnVisibilityModel: {
                                    mtid: false,
                                    mlType: !!this.props.mlVisibility,
                                    mlTagName: !!this.props.mlVisibility,
                                    mlTagId: false,
                                    mlStableName: !!this.props.mlVisibility,
                                    mlStableId: false,
                                    storeId: this.props.storeBrandVisibility,
                                    storeName: this.props.storeBrandVisibility,
                                    brandId: this.props.storeBrandVisibility,
                                    brandName: this.props.storeBrandVisibility,
                                    correctionPatId: false,
                                    channelMethod: false,
                                    channelPatId: false,
                                    storeMatchSource: false,
                                    storeNumPatId: false,
                                    mlTagNegative: false,
                                    mlConfScore: false,
                                },
                            },
                        }}
                        columns={columns}
                        disableRowSelectionOnClick
                        density='compact'
                        pagination={false}
                        onSortModelChange={(model) => {
                            if (this.columnDragging) {
                                return;
                            }

                            if (0 === model.length) {
                                // reset sort
                                this.props.onSort();

                                return;
                            }

                            this.props.onSort(
                                model[0].field as keyof MidTuple,
                                model[0].sort ? model[0].sort : undefined,
                            );
                        }}
                        sortingMode='server'
                        sortModel={this.props.sortModel}
                        checkboxSelection={this.props.checkboxSelection}
                        getRowClassName={(params) => {
                            if (params.row.affectedByActivePattern) {
                                return 'affectedByActivePattern';
                            }
                            if (params.row.mlTagId && this.props.tagHighlightsVisibility) {
                                return params.row.mlTagNegative
                                    ? 'tagNegative'
                                    : this.props.primaryStore?.id
                                    ? params.row.mlTagId === this.props.primaryStore.id
                                        ? 'tagMatch'
                                        : 'tagOther'
                                    : '';
                            }

                            return '';
                        }}
                        sx={{
                            '& .tagNegative': {
                                backgroundColor: '#F7F7F7',
                            },
                            '& .tagMatch': {
                                backgroundColor: '#EEFDF9',
                            },
                            '& .affectedByActivePattern': {
                                backgroundColor: '#FFF6F6',
                            },
                            '& .tagOther': {
                                backgroundColor: '#FFF6F6',
                            },
                        }}
                        onRowSelectionModelChange={this.props.onRowSelectionModelChange}
                    />
                </Box>
            </Box>
        );
    }

    private updateRegexExpr() {
        if (this.props.highlightPattern) {
            try {
                this.highlightRegex = new RegExp(this.props.highlightPattern, 'gd');

                return;
            } catch (err) {
                // invalid regex just ignore
            }
        }

        this.highlightRegex = undefined;
    }

    private withFillBtn(
        field: keyof PatternData,
        params: GridRenderCellParams<WithEsId<MidTuple>>,
        modelData?: string[] | string,
    ) {
        if (params.value === GeneralConstants.NULL_FIELD_VALUE) {
            return <em>{GeneralConstants.NULL_FIELD_DISPLAY}</em>;
        }
        if (!params.value) {
            return '';
        }

        if (!modelData) {
            // eslint-disable-next-line no-param-reassign
            modelData = params.value as string;
        }

        return (
            <React.Fragment>
                <TupleCell
                    params={params}
                    highlightField={this.props.highlightField}
                    highlightPattern={this.highlightRegex}
                    highlightChannel={this.props.highlightChannel}
                />
                {this.props.onFill && (
                    <Tooltip title='Fill Pattern'>
                        <IconButton
                            className='show-on-hover'
                            onClick={() => this.props.onFill?.(field, modelData)}
                            size='small'
                        >
                            <Icon fontSize='small'>input</Icon>
                        </IconButton>
                    </Tooltip>
                )}
                <Tooltip title='Copy to Clipboard'>
                    <IconButton
                        className='show-on-hover'
                        onClick={() => {
                            void navigator.clipboard.writeText((params.value as string | undefined) ?? '');
                        }}
                        size='small'
                    >
                        <Icon fontSize='small'>content_copy</Icon>
                    </IconButton>
                </Tooltip>
            </React.Fragment>
        );
    }

    private static storeColumnOrder(api: GridApi) {
        const orderData = api.getAllColumns().map((colDef) => colDef.field);

        localStorage.setItem(LocalStorageKeys.TUPLE_TABLE_COLS, JSON.stringify(orderData));
    }

    private static getOrderedColumns(
        defaultColumns: Array<GridColDef<WithEsId<MidTuple>>>,
    ): Array<GridColDef<WithEsId<MidTuple>>> {
        const order = TupleResultsTable.getStoredColumnOrder();
        if (!order) {
            return defaultColumns;
        }

        const finalColumns: Array<GridColDef<WithEsId<MidTuple>>> = [];
        const columnMap: Record<string, GridColDef<WithEsId<MidTuple>>> = {};

        defaultColumns.forEach((colDef) => (columnMap[colDef.field] = colDef));

        for (const field of order) {
            if (!columnMap[field]) {
                continue;
            }
            finalColumns.push(columnMap[field]);
            // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
            delete columnMap[field];
        }

        finalColumns.push(...Object.values(columnMap)); // push the rest of the columns

        return finalColumns;
    }

    private static getStoredColumnOrder(): string[] | undefined {
        const orderData = localStorage.getItem(LocalStorageKeys.TUPLE_TABLE_COLS);

        if (!orderData) {
            return undefined;
        }

        try {
            const order = JSON.parse(orderData) as string[];
            const predefinedOrder = [
                ...new Set([
                    GRID_CHECKBOX_SELECTION_COL_DEF.field,
                    'info',
                    'mlType',
                    'mlTagName',
                    'mlStableName',
                    ...order,
                ]),
            ];

            return predefinedOrder;
        } catch (error) {
            // todo logging
            return undefined;
        }
    }
}

const connected = React.memo(withDataGridApi(TupleResultsTable));
const MemoizedTupleResultsTable = React.memo(TupleResultsTable);
export { connected as TupleResultsTable, MemoizedTupleResultsTable };
